import { useState } from "react";

import type { Transaction } from "@/apis/gql/types";
import { useTable } from "@/components/shared/data-table/useTable";
import { type RawFilters, useServerSideQuery } from "@/hooks/useQueryFilters";
import { useTablePagination } from "@/hooks/useTablePagination";
import { AccountEntityType } from "@/lib/constants/account-types";
import { getExpandedRowModel } from "@/lib/table/row-expanding";
import { useGetAllJournalAccounts } from "@/queries/ledger/accounts/get-all";
import type { ExpandedState, Row } from "@tanstack/react-table";

import { columns } from "./columns";

export type FilterFields = "entityType";

export type ChartOfAccountsTableInstance = ReturnType<
  typeof useChartOfAccountsTable
>;

export function useChartOfAccountsTable({
  tableName,
  queryKeyPrefix,
  defaultFilters = {
    entityType: [
      AccountEntityType.BANK_ACCOUNT,
      AccountEntityType.EXPENSE_ACCOUNT,
    ],
  },
}: {
  tableName: string;
  queryKeyPrefix: string;
  defaultFilters?: RawFilters<FilterFields>;
}) {
  const [expanded, setExpanded] = useState<ExpandedState>({});
  const { page, setPage, limit, setLimit } = useTablePagination();

  const {
    globalSearch,
    setGlobalSearch,
    serverSideFilters,
    setFilters,
    filters,
    sorting,
    onSortingChange,
    sort,
    order,
    resetFilters,
    initialServerSideFilters,
  } = useServerSideQuery<FilterFields>({
    defaultFilters,
    queryKeyPrefix,
    defaultSorting: { sort: "createdAt", order: "desc" },
  });

  const { data, loading, error, pagination } = useGetAllJournalAccounts({
    query: serverSideFilters,
    global: globalSearch,
    page,
    limit,
    offset: (page - 1) * limit,
    sort,
    order,
  });

  const table = useTable({
    data: data ?? [],
    columns,
    tableName,
    expanded,
    onExpandedChange: setExpanded,
    getRowId: (row) => row.journalAccountId,
    getExpandedRowModel,
    sorting,
    onSortingChange,
    manualFiltering: true,
    manualSorting: true,
    manualPagination: true,
    initialServerSideFilters,
    onServerSideFiltersClear: resetFilters,
    serverSideFilters,
    globalServerSideSearch: globalSearch,
  });

  const handleRowClick = (
    event: React.MouseEvent,
    row: Row<Transaction>,
    index: number,
  ) => {
    table.table.setFocusedRow(row.id);
  };

  const handlePageChange = (page: number) => {
    setPage(page);
  };

  const handlePageSizeChange = (pageSize: number) => {
    setLimit(pageSize);
  };

  return {
    ...table,
    loading,
    error,
    pagination,
    handleRowClick,
    handlePageChange,
    handlePageSizeChange,
    expanded,
    setExpanded,
    globalSearch,
    setGlobalSearch,
    filters,
    setFilters,
    limit,
  };
}
